import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Divider
} from '@mui/material';

const DonationTransparencyPage = () => {
  const { t } = useTranslation();

  // 動畫效果
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeInOut" },
    },
  };

  // 各部分視圖檢測
  const [introRef, introInView] = useInView({ threshold: 0.2 });
  const [usageRef, useSecInView] = useInView({ threshold: 0.2 });
  const [reportRef, reportInView] = useInView({ threshold: 0.2 });

  // 範例捐款資料
  const donationData = [
    { id: 1, date: '2025-01-10', amount: 5000, project: 'donationTransparency.antiScamTech' },
    { id: 2, date: '2025-01-25', amount: 8000, project: 'donationTransparency.websiteDevelopment' },
    { id: 3, date: '2025-02-05', amount: 12000, project: 'donationTransparency.educationProgram' },
    { id: 4, date: '2025-02-18', amount: 7500, project: 'donationTransparency.serverMaintenance' },
    { id: 5, date: '2025-03-01', amount: 15000, project: 'donationTransparency.aiRiskPrediction' },
  ];

  return (
    <Box className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen pb-12">
      {/* Hero Banner 區塊 */}
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold shadow-lg"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        <h1 className="text-white uppercase tracking-wide">
          {t('donationTransparency')}
        </h1>
        <p className="text-lg font-medium mt-2 text-gray-200">
          {t('donationTransparency.subtitle')}
        </p>
      </div>

      <Container maxWidth="lg" sx={{ mt: 6 }}>
        {/* 介紹部分 */}
        <motion.div
          ref={introRef}
          initial="hidden"
          animate={introInView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              mb: 6, 
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(138, 75, 175, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0.03,
                backgroundImage: `url(/images/bubble.png)`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                pointerEvents: 'none',
                zIndex: 0
              }} 
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: 'linear-gradient(to right, #8B5CF6, #6366F1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('donationTransparency.introduction')}
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.05rem' }}>
                {t('donationTransparency.introText')}
              </Typography>
            </Box>
          </Paper>
        </motion.div>

        {/* 捐款用途 */}
        <motion.div
          ref={usageRef}
          initial="hidden"
          animate={useSecInView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              mb: 6, 
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(138, 75, 175, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: 'linear-gradient(to right, #8B5CF6, #6366F1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('donationTransparency.howWeUse')}
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, backgroundColor: 'rgba(138, 75, 175, 0.05)', borderRadius: '12px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#6a0dad' }}>
                      {t('donationTransparency.platformDevelopment')}
                    </Typography>
                    <Typography variant="body1">
                      {t('donationTransparency.platformDevelopmentText')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, backgroundColor: 'rgba(138, 75, 175, 0.05)', borderRadius: '12px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#6a0dad' }}>
                      {t('donationTransparency.educationInitiatives')}
                    </Typography>
                    <Typography variant="body1">
                      {t('donationTransparency.educationInitiativesText')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, backgroundColor: 'rgba(138, 75, 175, 0.05)', borderRadius: '12px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#6a0dad' }}>
                      {t('donationTransparency.researchPrograms')}
                    </Typography>
                    <Typography variant="body1">
                      {t('donationTransparency.researchProgramsText')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, backgroundColor: 'rgba(138, 75, 175, 0.05)', borderRadius: '12px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#6a0dad' }}>
                      {t('donationTransparency.scamPreventionTools')}
                    </Typography>
                    <Typography variant="body1">
                      {t('donationTransparency.scamPreventionToolsText')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </motion.div>

        {/* 捐款報告 */}
        <motion.div
          ref={reportRef}
          initial="hidden"
          animate={reportInView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 5 }, 
              mb: 6, 
              borderRadius: '16px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(138, 75, 175, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background: 'linear-gradient(to right, #8B5CF6, #6366F1)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('donationTransparency.donationReport')}
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                {t('donationTransparency.donationReportText')}
              </Typography>
              
              <TableContainer>
                <Table aria-label="donation transparency table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('donationTransparency.date')}</TableCell>
                      <TableCell align="right">{t('donationTransparency.amount')} (NT$)</TableCell>
                      <TableCell>{t('donationTransparency.usage')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donationData.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          {row.date}
                        </TableCell>
                        <TableCell align="right">{row.amount.toLocaleString()}</TableCell>
                        <TableCell>{t(row.project)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('donationTransparency.totalDonation')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#6a0dad' }}>
                  NT$ {donationData.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* 致謝 */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            mb: 6, 
            borderRadius: '16px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(138, 75, 175, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: theme => theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
            textAlign: 'center'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              {t('donationTransparency.thankYou')}
            </Typography>
            
            <Typography variant="body1">
              {t('donationTransparency.thankYouText')}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DonationTransparencyPage; 